import { toast } from 'sonner';
import { isPostingSurfaceWatched } from '../hooks/usePostingSurfacePresence';
import { isHydratingNotifications } from '../hooks/useNotificationEventStream';
import { notificationHref } from './entityHref';
import { messageFor, planFor } from './notifyRouting';
import { useNotificationStore } from '../store/notificationStore';
import type { Notification } from '../types/notification.types';

/**
 * N8 (86d3w2kez) part 3 — THE ONE PLACE A POSTING OUTCOME BECOMES A TOAST.
 *
 * ⛔ <b>Do not call `toast.*` directly for a posting outcome anywhere in feature code.</b> Two
 * independent outcome toasts already existed before this — `useSalesOrderPostingToasts` and a block in
 * `QuotationDetailPanel` — and both are deleted by this ticket, because with the notification centre
 * live they would each fire alongside this one and every SO and CL post would double-toast.
 *
 * <b>Imperative click-acks are a different thing and stay.</b> "Posting started", "Retrying…", the
 * result of a cancel — those fire on the user's own click, at a different moment from settle, and they
 * are what satisfies the sub-100ms feedback gate. They are not outcomes and do not belong here.
 *
 * Three suppression rules, in the order they are checked:
 *
 * 1. <b>Not an outcome.</b> `queued`/`posting` produce no plan — see `kindForState`.
 * 2. <b>Hydrating.</b> After a dropped connection the client catches up on everything it missed. A
 *    laptop waking from sleep must not throw twenty toasts at its owner.
 * 3. <b>Already watching.</b> The record's own page is open and focused, so the card on screen has
 *    already said it.
 */
/**
 * The router's `navigate`, injected by AppLayout at mount. This module is plain TypeScript — no hook
 * context — but its Open action must be an SPA navigation: `window.location.assign` is a full
 * document reload, the exact pattern this codebase deliberately removed from session-end because it
 * "destroyed unsaved work and reset the route" — and it would also tear down the app-lifetime store
 * and the SSE connection, consuming the arrival flash before the page could render it
 * (2026-08-25 review). The assign fallback survives only for the never-mounted edge.
 */
let navigateRef: ((to: string) => void) | null = null;

export function setNotificationNavigator(navigate: (to: string) => void): void {
  navigateRef = navigate;
}

export function notifyPostingOutcome(n: Notification): void {
  const plan = planFor(n);
  if (!plan) return;

  if (isHydratingNotifications()) return;
  if (isPostingSurfaceWatched(n.type, n.entityId)) return;

  const { title, description } = messageFor(n);
  const href = notificationHref(n);

  // ⚠️ `id` is what makes this an UPDATE rather than a new toast. Sonner replaces a toast it already
  // holds under that id, so one posting owns one toast for its whole life however many frames it
  // emits — which is the difference between a retry storm being one line and being a wall.
  const options = {
    id: plan.id,
    duration: plan.duration ?? Infinity,
    description,
    // Only a terminal failure earns a button, and Open is the useful one: it lands on the record with
    // the arrival flash, where the full recovery card and its reach disclosure live. Recovering from
    // a toast would mean re-deriving reach in a third place.
    action:
      plan.actions.includes('open') && href
        ? {
            label: 'Open',
            onClick: () => {
              // Opening from the toast is reading, same as opening from the row (CLAUDE_CODE_BRIEF
              // §109: "Row/toast click … marks read"). Fire-and-forget for the same reason as the
              // row: markRead restores itself on failure and must never block the navigation.
              if (n.unread) {
                void useNotificationStore
                  .getState()
                  .markRead([n.id])
                  .catch(() => {});
              }
              if (navigateRef) navigateRef(href);
              else window.location.assign(href);
            },
          }
        : undefined,
  };

  switch (plan.kind) {
    case 'success':
      toast.success(title, options);
      return;
    case 'terminal':
      toast.error(title, options);
      return;
    case 'retryable':
      toast.warning(title, options);
      return;
    default:
      toast(title, options);
  }
}
